import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FaDownload, FaLink, FaPaperPlane, FaPlus, FaSyncAlt, FaTrash } from 'react-icons/fa';
import { FaRegFilePdf } from 'react-icons/fa6';
import { BsFiletypePng, BsFiletypeJpg, BsImage } from 'react-icons/bs';
import { FiUploadCloud } from 'react-icons/fi';
import { toast } from '../../toasts';
import documentService from '../../services/document.service';
import esignatureService from '../../services/esignature.service';
import { getObjectServiceConfig } from '../../constants/objectServiceConfig';
import { usePermissions } from '../../hooks/usePermissions';
import { getError } from '../../utils';
import { isEsignatureEnabled } from '../../utils/featureFlags';
import DetailPageLoader from '../DetailPageLoader/DetailPageLoader';
import { Button, EmptyState, Pill } from '../EntityList';
import { getEsignatureStatusTone } from '../../constants/esignatureStatus';
import DocumentEditor from '../DocumentEditor/DocumentEditor';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import SendForSignatureModal from '../SendForSignatureModal/SendForSignatureModal';
import LinkExistingSignatureModal from '../LinkExistingSignatureModal/LinkExistingSignatureModal';
import { useLabels } from '../../hooks/useLabels';

// Mongo ObjectIds encode their creation time in their first 4 bytes. The
// Document model has no `createdAt` (no `{ timestamps: true }`) and no size
// field at all, so this is the only "fecha" we can show without a backend
// change; there is no real "peso" (file size) to show, see the file-type
// label used in its place below.
const dateFromObjectId = (id) => {
	if (!id || typeof id !== 'string' || id.length < 8) return null;
	const seconds = parseInt(id.substring(0, 8), 16);
	if (Number.isNaN(seconds)) return null;
	return new Date(seconds * 1000);
};

const formatDate = (date) => {
	if (!date) return '';
	return date.toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' });
};

const EXTENSION_LABEL = {
	pdf: 'PDF',
	png: 'PNG',
	jpg: 'JPG',
};

const TILE_TONE_CLASSES = {
	pdf: 'tw:bg-danger-soft tw:text-danger',
	png: 'tw:bg-accent-soft tw:text-accent-ink',
	jpg: 'tw:bg-accent-soft tw:text-accent-ink',
	default: 'tw:bg-surface-3 tw:text-ink-2',
};

const getFileExtension = (fileLink) => {
	if (!fileLink) return '';
	let rawExt = '';
	try {
		const url = new URL(fileLink);
		const pathname = url.pathname;
		rawExt = pathname.split('.').pop()?.toLowerCase() || '';
	} catch (error) {
		const parts = fileLink.split('.');
		if (parts.length > 1) {
			rawExt = parts.pop()?.toLowerCase() || '';
		}
	}

	if (rawExt.includes('pdf')) return 'pdf';
	if (rawExt.includes('png')) return 'png';
	if (rawExt.includes('jpg') || rawExt.includes('jpeg')) return 'jpg';

	return rawExt;
};

const renderFileIcon = (fileLink) => {
	const extension = getFileExtension(fileLink);
	switch (extension) {
	case 'pdf':
		return <FaRegFilePdf title="PDF" size="1.15em" />;
	case 'png':
		return <BsFiletypePng title="PNG" size="1.15em" />;
	case 'jpg':
		return <BsFiletypeJpg title="JPG" size="1.15em" />;
	default:
		return <BsImage title={extension || 'Archivo'} size="1.15em" />;
	}
};

/**
 * Adjuntos tab. `documents` is optional: pass the parent's already-fetched
 * `documents` array (e.g. from AccountDetail's own account fetch) to avoid
 * this component re-fetching the whole parent object just to read that one
 * field. When omitted, it falls back to the original self-fetch via
 * `config.service.getDetails(objectId)` for other callers that don't have the
 * parent object loaded already.
 *
 * `object` is likewise optional and only meaningful alongside `documents`:
 * pass the parent's already-fetched record (the same one `documents` was
 * read off of) so the "Nuevo Documento" modal's associated-object picker can
 * show its real name/customId instead of falling back to the bare id — the
 * self-fetch path below already has the full record and doesn't need this.
 */
const SingleObjectDocumentManager = ({ objectId, objectType, documentType, documents: documentsProp, object: objectProp, onDocumentsChange }) => {
	const { t } = useLabels();
	const hasDocumentsProp = Array.isArray(documentsProp);
	const [object, setObject] = useState(objectProp || null);
	const [documents, setDocuments] = useState([]);
	const [loading, setLoading] = useState(!hasDocumentsProp);
	const [error, setError] = useState('');
	const [showEditor, setShowEditor] = useState(false);
	const [editingDocument, setEditingDocument] = useState(null);
	const [deletingDocument, setDeletingDocument] = useState(null);
	const [dragOver, setDragOver] = useState(false);
	const [signingDocument, setSigningDocument] = useState(null);
	const [linkingDocument, setLinkingDocument] = useState(null);
	const [refreshingId, setRefreshingId] = useState(null);
	const { canDelete } = usePermissions();

	const filterDocuments = useCallback((rawDocuments) => {
		let filtered = (rawDocuments || []).filter((doc) => doc && typeof doc === 'object');
		if (documentType) {
			filtered = filtered.filter((doc) => doc.documentType === documentType);
		}
		return filtered;
	}, [documentType]);

	// Self-fetch path (no `documents` prop): fetch the parent object once, its
	// `documents` array comes back populated from the backend.
	const fetchObjectAndDocuments = useCallback(async () => {
		setLoading(true);
		setError('');
		try {
			const config = getObjectServiceConfig(objectType);
			if (!config) {
				setError('Tipo de objeto no válido');
				setLoading(false);
				return;
			}

			const objectResponse = await config.service.getDetails(objectId);
			const fetchedObject = objectResponse.data;
			setObject(fetchedObject);
			setDocuments(filterDocuments(fetchedObject?.documents));
		} catch (err) {
			setError(err.response?.data?.message || 'Error al cargar documentos');
			setDocuments([]);
		} finally {
			setLoading(false);
		}
	}, [objectId, objectType, filterDocuments]);

	useEffect(() => {
		if (hasDocumentsProp) {
			// Parent already has the object loaded (and its documents array);
			// just filter/present it, no duplicate object fetch. Fall back to
			// an id-only stub if the caller didn't also pass `object` — the
			// associated-object Select just shows the id in that case.
			setObject(objectProp || { _id: objectId });
			setDocuments(filterDocuments(documentsProp));
			setLoading(false);
			setError('');
			return;
		}
		if (objectId && objectType) {
			fetchObjectAndDocuments();
		}
	}, [objectId, objectType, hasDocumentsProp, documentsProp, objectProp, filterDocuments, fetchObjectAndDocuments]);

	// After a mutation: ask the parent to refresh its copy when it owns the
	// data, otherwise fall back to the original self-fetch.
	const refreshDocuments = useCallback(() => {
		if (hasDocumentsProp) {
			onDocumentsChange?.();
		} else {
			fetchObjectAndDocuments();
		}
	}, [hasDocumentsProp, onDocumentsChange, fetchObjectAndDocuments]);

	const handleAddDocument = (file) => {
		// Create a pseudo initialData object with the preselected objectId
		// (and, when opened via drop, the dropped file pre-attached).
		const config = getObjectServiceConfig(objectType);
		const preselectedData = config ? {
			[config.fieldName]: objectId,
		} : {};
		setEditingDocument(file ? { ...preselectedData, file } : preselectedData);
		setShowEditor(true);
	};

	const handleEditDocument = async (document) => {
		setEditingDocument(document);
		setShowEditor(true);
	};

	const handleEditorClose = () => {
		setShowEditor(false);
		setEditingDocument(null);
	};

	const handleEditorSuccess = () => {
		refreshDocuments();
	};

	const handleDeleteDocument = (document) => {
		setDeletingDocument(document);
	};

	const handleCloseDeleteModal = () => {
		setDeletingDocument(null);
	};

	const handleConfirmDelete = async () => {
		if (!deletingDocument) {
			return;
		}
		try {
			const relatedType = getObjectServiceConfig(objectType)?.fieldName || objectType;
			await documentService.delete(deletingDocument._id, objectId, relatedType);
			toast.success('Documento eliminado');
			refreshDocuments();
		} catch (err) {
			toast.error(getError(err));
		}
	};

	const handleSendForSignature = (document) => {
		setSigningDocument(document);
	};

	const handleSignatureSent = () => {
		setSigningDocument(null);
		refreshDocuments();
	};

	const handleLinkExisting = (document) => {
		setLinkingDocument(document);
	};

	const handleSignatureLinked = () => {
		setLinkingDocument(null);
		refreshDocuments();
	};

	const handleRefreshSignature = async (document) => {
		setRefreshingId(document._id);
		try {
			await esignatureService.refresh(document._id);
			refreshDocuments();
		} catch (err) {
			toast.error(getError(err));
		}
		setRefreshingId(null);
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		setDragOver(true);
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		setDragOver(false);
	};

	const handleDrop = (e) => {
		e.preventDefault();
		setDragOver(false);
		const file = e.dataTransfer.files?.[0];
		if (file) {
			handleAddDocument(file);
		}
	};

	const handleFileInputChange = (e) => {
		const file = e.target.files?.[0];
		if (file) {
			handleAddDocument(file);
		}
		e.target.value = '';
	};

	return (
		<div className="tw:space-y-4">
			<div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
				<h3 className="tw:m-0 tw:text-[15px] tw:font-semibold tw:text-ink-0">Documentos</h3>
				<Button variant="primary" icon={<FaPlus />} onClick={() => handleAddDocument()}>
					Nuevo Documento
				</Button>
			</div>

			<label
				className={classnames(
					'tw:flex tw:cursor-pointer tw:items-center tw:justify-center tw:gap-2 tw:rounded-panel tw:border-2 tw:border-dashed tw:px-4 tw:py-6 tw:text-[13px] tw:transition-colors tw:duration-100',
					dragOver
						? 'tw:border-accent tw:bg-accent-soft tw:text-accent-ink'
						: 'tw:border-border-strong tw:text-ink-2 tw:hover:border-ink-3 tw:hover:bg-surface-2',
				)}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<FiUploadCloud className="tw:text-[18px]" />
				<span>
					Arrastrá un archivo aquí o <span className="tw:font-semibold tw:underline">elegí uno</span>
				</span>
				<input
					type="file"
					className="tw:sr-only"
					onChange={handleFileInputChange}
					accept="application/pdf,image/*,.doc,.docx"
				/>
			</label>

			{loading ? (
				<DetailPageLoader entityLabel="documentos" />
			) : error ? (
				<div className="tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger">
					{error}
				</div>
			) : documents.length === 0 ? (
				<EmptyState title="No hay documentos" description="Subí un archivo con el botón de arriba o arrastrándolo a la zona de carga." />
			) : (
				<div className="tw:rounded-panel tw:border tw:border-border tw:bg-surface">
					{documents.map((document) => {
						const extension = getFileExtension(document.fileLink);
						const dateLabel = formatDate(dateFromObjectId(document._id));
						const typeLabel = EXTENSION_LABEL[extension] || (extension ? extension.toUpperCase() : 'Archivo');
						const subline = [typeLabel, dateLabel].filter(Boolean).join(' · ');

						return (
							<div
								key={document._id}
								className="tw:group tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:border-b tw:border-border tw:px-4 tw:py-2.5 tw:last:border-b-0 tw:hover:bg-surface-2"
								onClick={() => handleEditDocument(document)}
							>
								<span
									className={classnames(
										'tw:flex tw:h-10 tw:w-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[7px]',
										TILE_TONE_CLASSES[extension] || TILE_TONE_CLASSES.default,
									)}
								>
									{renderFileIcon(document.fileLink)}
								</span>
								<div className="tw:min-w-0 tw:flex-1">
									<div className="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
										<span className="tw:truncate tw:text-[13.5px] tw:font-medium tw:text-ink-0">
											{document.name || 'Sin nombre'}
										</span>
										{document.esignature?.status && (
											<span className='tw:shrink-0'>
												<Pill tone={getEsignatureStatusTone(document.esignature.status)}>
													{t(`esignature.status.${document.esignature.status}`)}
												</Pill>
											</span>
										)}
									</div>
									<div className="tw:truncate tw:text-[12px] tw:text-ink-3">{subline}</div>
								</div>
								<div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
									{document.fileLink && (
										<a
											className="tw:flex tw:h-[26px] tw:w-[26px] tw:items-center tw:justify-center tw:rounded-full tw:text-ink-2 tw:transition-colors tw:duration-100 tw:hover:bg-surface-3 tw:hover:text-accent-ink"
											href={document.fileLink}
											target="_blank"
											rel="noopener noreferrer"
											title="Descargar documento"
											onClick={(e) => e.stopPropagation()}
										>
											<FaDownload size="0.9em" />
										</a>
									)}
									{isEsignatureEnabled() && !document.esignature?.status && (
										<button
											type="button"
											className="tw:flex tw:h-[26px] tw:w-[26px] tw:items-center tw:justify-center tw:rounded-full tw:text-ink-2 tw:transition-colors tw:duration-100 tw:hover:bg-surface-3 tw:hover:text-accent-ink"
											onClick={(e) => {
												e.stopPropagation();
												handleSendForSignature(document);
											}}
											title={t('esignature.send')}
										>
											<FaPaperPlane size="0.85em" />
										</button>
									)}
									{isEsignatureEnabled() && !document.esignature?.status && (
										<button
											type="button"
											className="tw:flex tw:h-[26px] tw:w-[26px] tw:items-center tw:justify-center tw:rounded-full tw:text-ink-2 tw:transition-colors tw:duration-100 tw:hover:bg-surface-3 tw:hover:text-accent-ink"
											onClick={(e) => {
												e.stopPropagation();
												handleLinkExisting(document);
											}}
											title={t('esignature.link.title')}
										>
											<FaLink size="0.85em" />
										</button>
									)}
									{isEsignatureEnabled() && document.esignature?.status === 'pending' && (
										<button
											type="button"
											className="tw:flex tw:h-[26px] tw:w-[26px] tw:items-center tw:justify-center tw:rounded-full tw:text-ink-2 tw:transition-colors tw:duration-100 tw:hover:bg-surface-3 tw:hover:text-accent-ink"
											onClick={(e) => {
												e.stopPropagation();
												handleRefreshSignature(document);
											}}
											disabled={refreshingId === document._id}
											title={t('esignature.refresh')}
										>
											<FaSyncAlt size="0.85em" className={refreshingId === document._id ? 'fa-spin' : ''} />
										</button>
									)}
									{canDelete() && (
										<button
											type="button"
											className="tw:flex tw:h-[26px] tw:w-[26px] tw:items-center tw:justify-center tw:rounded-full tw:text-ink-2 tw:transition-colors tw:duration-100 tw:hover:bg-danger-soft tw:hover:text-danger"
											onClick={(e) => {
												e.stopPropagation();
												handleDeleteDocument(document);
											}}
											title="Eliminar documento"
										>
											<FaTrash size="0.85em" />
										</button>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}

			<DocumentEditor
				show={showEditor}
				onHide={handleEditorClose}
				onSuccess={handleEditorSuccess}
				initialData={editingDocument}
				documentType={documentType}
				objectType={objectType}
				objects={object ? [object] : []}
				readonlyObject={true}
			/>
			<ConfirmationModal
				show={!!deletingDocument}
				handleClose={handleCloseDeleteModal}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteDocument')}
				description={t('msg.confirmDeleteDocument')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
			{isEsignatureEnabled() && (
				<SendForSignatureModal
					show={!!signingDocument}
					onHide={() => setSigningDocument(null)}
					document={signingDocument}
					objectType={objectType}
					objectId={objectId}
					onSent={handleSignatureSent}
				/>
			)}
			{isEsignatureEnabled() && (
				<LinkExistingSignatureModal
					show={!!linkingDocument}
					onHide={() => setLinkingDocument(null)}
					document={linkingDocument}
					objectType={objectType}
					objectId={objectId}
					onLinked={handleSignatureLinked}
				/>
			)}
		</div>
	);
};

SingleObjectDocumentManager.propTypes = {
	objectId: PropTypes.string,
	objectType: PropTypes.string,
	documentType: PropTypes.string,
	documents: PropTypes.array,
	object: PropTypes.object,
	onDocumentsChange: PropTypes.func,
};

export default SingleObjectDocumentManager;
